import { Button, Popover, PopoverContent, PopoverTrigger, RangeCalendar, Select, SelectItem, Tab, Tabs, Tooltip } from '@heroui/react'
import { parseDate } from '@internationalized/date'
import { useDateFormatter } from '@react-aria/i18n'
import { useQuery } from '@tanstack/react-query'
import PhoneSelect from 'components/Hero/PhoneSelect'
import { formatDistanceToNow } from 'date-fns'
import { Calendar, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { useToggle } from 'usehooks-ts'
import { $fetch } from 'utils/fetch'
import { presets } from '../utils/rangePresets'

export default function FilterHeader({ filters, setFilters, tags }) {
  const [isCalendarOpen, toggleCalendar] = useToggle(false)
  const [selectedPreset, setSelectedPreset] = useState('lastWeek')

  const { data: pathways = [], isLoading: pathwaysLoading } = useQuery({
    queryKey: ['pathways'],
    queryFn: () => $fetch('/v1/pathway'),
    staleTime: 1000 * 60 * 10,
  })

  const handlePathwaySelection = (keys) => {
    const selectedKey = Array.from(keys)[0]
    setFilters({ batchId: null, pathwayId: selectedKey })
  }

  const defaultFilters = {
    start: filters.start,
    end: filters.end,
    direction: 'all',
  }

  const handleClearFilters = () => {
    setFilters(Object.keys(filters).reduce((acc, key) => {
      if (key in defaultFilters) {
        acc[key] = defaultFilters[key]
      }
      else {
        acc[key] = null
      }
      return acc
    }, {}))
  }

  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === 'direction') {
        return value !== 'all'
      }
      else if (key === 'start' || key === 'end') {
        return false
      }
      return !!value
    })
  }, [filters])

  const { data: { data: batches } = {}, isLoading: batchesLoading } = useQuery({
    queryKey: ['batches'],
    queryFn: () => $fetch('/v2/batches/list'),
    staleTime: 1000 * 60 * 10,
  })

  const handleBatchSelection = (keys) => {
    const selectedKey = Array.from(keys)[0]
    if (selectedKey) {
      const selectedBatch = batches?.find(p => p.id === selectedKey)
      const createdAtString = selectedBatch.created_at.split('T')[0]
      const batchDate = parseDate(createdAtString)

      setFilters({
        pathwayId: null,
        batchId: selectedKey,
        start: batchDate,
        end: batchDate,
      })
    }
    else {
      setFilters({ batchId: null })
    }
  }

  const handleTagSelection = (keys) => {
    const selectedKey = Array.from(keys)[0]
    setFilters({ pathwayTag: selectedKey })
  }

  const handleDirectionSelection = (keys) => {
    const selectedKey = Array.from(keys)[0]
    setFilters({ direction: selectedKey, to: null, from: null, batchId: null })
  }

  const handleDateChange = (value) => {
    const start = value.start.toDate()
    const end = value.end.toDate()
    const diffDays = (end - start) / (1000 * 60 * 60 * 24)
    if (diffDays > 60) {
      toast.error('Date range should not exceed 60 days')
    }
    else {
      setSelectedPreset('custom')
      setFilters(value)
      toggleCalendar()
    }
  }

  const handlePresetSelection = (preset) => {
    setSelectedPreset(preset)
    if (preset === 'custom') {
      toggleCalendar()
    }
    else {
      setFilters(presets[preset])
    }
  }

  const formatter = useDateFormatter({ dateStyle: 'short' })

  const customDateRange = useMemo(() => {
    return formatter.formatRange(
      filters.start.toDate(),
      filters.end.toDate(),
    )
  }, [filters.start, filters.end])

  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center">
        <Tabs
          variant="light"
          classNames={{ tabList: 'rounded-sm' }}
          radius="sm"
          size="sm"
          aria-label="Date Range Presets"
          selectedKey={selectedPreset}
          onSelectionChange={handlePresetSelection}
        >
          <Tab key="today" title="Today" />
          <Tab key="lastWeek" title="Last week" />
          <Tab key="last2Months" title="Last 2 months" />
          <Tab
            key="custom"
            title={(
              <span className="flex items-center gap-2">
                <Calendar size={16} className="text-default-500" />
                {selectedPreset === 'custom' && (
                  <span className="truncate text-default-500">{customDateRange}</span>
                )}
              </span>
            )}
          />
        </Tabs>
        <Popover isOpen={isCalendarOpen} onOpenChange={toggleCalendar} placement="bottom" classNames={{ content: 'rounded-md p-0' }}>
          <PopoverTrigger className="w-0 min-w-0 px-0">
            <Button className="invisible w-0 min-w-0" />
          </PopoverTrigger>
          <PopoverContent>
            <RangeCalendar
              visibleMonths={2}
              radius="sm"
              classNames={{
                base: 'rounded-md',
              }}
              value={{
                start: parseDate(filters.start.toString()),
                end: parseDate(filters.end.toString()),
              }}
              onChange={handleDateChange}
              maxValue={presets.today.end}
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex items-center gap-3">
        <Select
          selectedKeys={filters.direction ? [filters.direction] : []}
          className="w-24"
          size="sm"
          variant="flat"
          radius="sm"
          onSelectionChange={handleDirectionSelection}
          disallowEmptySelection
          defaultSelectedKeys={['all']}
          classNames={{
            base: 'min-w-[80px] w-fit shrink-0',
            popoverContent: 'w-64! rounded-sm',
            innerWrapper: 'w-full',
          }}
        >
          <SelectItem key="all" title="All" />
          <SelectItem key="inbound" title="Inbound" />
          <SelectItem key="outbound" title="Outbound" />
        </Select>

        {filters.direction !== 'all' && (
          <PhoneSelect
            value={filters.direction === 'inbound' ? filters.to : filters.from}
            onChange={number =>
              setFilters({ [filters.direction === 'inbound' ? 'to' : 'from']: number })}
            variant="flat"
            size="sm"
            radius="sm"
            hideLabel
            hideDescription
            placeholder="Filter by number"
            classNames={{
              base: 'min-w-[80px] w-fit shrink-0',
              popoverContent: 'w-64! rounded-sm',
              innerWrapper: 'w-full',
            }}
          />
        )}

        <Select
          placeholder="Filter by Pathway"
          selectedKeys={filters.pathwayId ? [filters.pathwayId] : []}
          isLoading={pathwaysLoading}
          className="w-48"
          size="sm"
          variant="flat"
          radius="sm"
          onSelectionChange={handlePathwaySelection}
          classNames={{
            base: 'min-w-[80px] w-fit shrink-0',
            popoverContent: 'w-64! rounded-sm',
            innerWrapper: 'w-full',
          }}
        >
          {pathways?.map(p => (
            <SelectItem key={p.id} description={p.description}>
              {p.name}
            </SelectItem>
          ))}
        </Select>

        {filters.direction === 'outbound' && (
          <Select
            placeholder="Filter by Batch"
            selectedKeys={filters.batchId ? [filters.batchId] : []}
            isLoading={batchesLoading}
            className="w-48"
            size="sm"
            variant="flat"
            radius="sm"
            onSelectionChange={handleBatchSelection}
            classNames={{
              base: 'min-w-[80px] w-fit shrink-0',
              popoverContent: 'w-64! rounded-sm',
              innerWrapper: 'w-full',
            }}
          >
            {batches?.map(p => (
              <SelectItem
                key={p.id}
                description={formatDistanceToNow(new Date(p.created_at), { addSuffix: true })}
              >
                {p.description}
              </SelectItem>
            ))}
          </Select>
        )}

        {tags?.length > 0 && (
          <Select
            aria-label="Tags"
            placeholder="Filter by Tag"
            className="w-48"
            size="sm"
            variant="flat"
            radius="sm"
            onSelectionChange={handleTagSelection}
            selectedKeys={filters.pathwayTag ? [filters.pathwayTag] : []}
            renderValue={(selectedKeys) => {
              return selectedKeys[0].rendered
            }}
            classNames={{
              base: 'min-w-[80px] w-fit shrink-0',
              popoverContent: 'w-64! rounded-sm',
              innerWrapper: 'w-full',
            }}
          >
            {tags?.map(tagMetric => (
              <SelectItem key={JSON.stringify(tagMetric.tag)} description={`${tagMetric.count} calls`}>
                <div className="flex flex-row items-center gap-2">
                  <div className="size-3 shrink-0 rounded-full" style={{ backgroundColor: tagMetric.tag.color ?? 'gray' }} />
                  <span className="truncate">{tagMetric.tag.name}</span>
                </div>
              </SelectItem>
            ))}
          </Select>
        )}

        {hasActiveFilters && (
          <Tooltip content="Clear filters" placement="right">
            <Button isIconOnly variant="light" size="sm" radius="sm" onPress={handleClearFilters}>
              <X size={12} />
            </Button>
          </Tooltip>
        )}
      </div>

    </div>
  )
}
